/* ⚠️ GENERATED from app/services/stories.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   📸 stories.ts — Stories: a photo or a short video for 24 hours.

   One rule for the server (which decides who sees which story and keeps the
   viewers) and both clients (which make, order and play them).

   THE DOCUMENT — stories/{id}
     userId, userName, userImage, createdAt, expiresAt (createdAt + 24 h)
     type 'image' | 'video', url, thumbUrl, storagePath, thumbPath, w, h, durationMs
     audience 'friends' (default) | 'everyone'
     + the editor's look, exactly the fields a Vibe post carries (STORY_EDIT_KEYS):
       filter, drawn-on text, stickers, the song and its mix
     viewCount — server-owned
   stories/{id}/viewers/{uid}  { uid, at, r? }   — the server writes; only the owner reads
   stories/{id}/replies/{auto} { uid, name, image, text, at } — the same
   storyViews/{uid}            { seen: { storyId: at } } — what I have watched

   🟢 PRIVACY IS THE SERVER'S: clients cannot read stories at all except their
      own (MERGED_RULES); the server hands each viewer the stories they may see
      (storyRoutes.feed) — friends-only for anybody else's friends, never across
      a block.
   ═══════════════════════════════════════════════════════════════════════════ */

export const STORY_TTL_MS = 24 * 60 * 60 * 1000;
export const IMAGE_MS = 5000;
export const VIDEO_MAX_MS = 30000;
export const STORY_TEXT_MAX = 500;
export const STORY_BATCH_MAX = 10;
export const STORY_REACTIONS = ['❤️', '😂', '😮', '😢', '👏', '🔥'];

/** What the editor's look may put on a story (and nothing else). */
export const STORY_EDIT_KEYS = ['filterName', 'filterColor', 'filterIntensity', 'overlayText', 'overlayTextColor', 'overlayTextStyle', 'overlayTextBg', 'overlayTextAlign', 'overlayTextSize', 'overlayTextFont', 'overlayTextX', 'overlayTextY', 'stickers', 'audioTrackUrl', 'audioTrackTitle', 'audioTrackArtist', 'audioTrackLicense', 'audioTrackPage', 'audioTrackProvider', 'audioTrackId', 'audioStartMs', 'isOriginalMuted', 'isAddedSoundMuted', 'originalVolume', 'addedSoundVolume', /* the browser's editor trims by marking, not cutting — the player honours it */
'trimStartMs', 'trimEndMs'];

/** The editor's item → the look a story keeps (undefined dropped: Firestore refuses it). */
export function storyLook(item) {
  const out = {};
  const it = item || {};
  for (const k of STORY_EDIT_KEYS) {
    const v = it[k];
    if (v === undefined) continue;
    out[k] = v;
  }
  if (Array.isArray(out.stickers)) {
    /* a sticker that is only a file on this phone cannot be seen by anyone else */
    out.stickers = out.stickers.filter(s => s && !(s.type === 'gallery_image' && s.imageUrl && !/^https?:/i.test(String(s.imageUrl))));
  }
  if (it.type !== 'video') {
    for (const k of ['audioTrackUrl', 'audioTrackTitle', 'audioTrackArtist', 'audioTrackLicense', 'audioTrackPage', 'audioTrackProvider', 'audioTrackId', 'audioStartMs', 'isOriginalMuted', 'isAddedSoundMuted', 'originalVolume', 'addedSoundVolume', 'trimStartMs', 'trimEndMs']) delete out[k];
  }
  return out;
}
const msOf = t => {
  if (!t) return 0;
  if (typeof t === 'number') return t;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t._seconds === 'number') return t._seconds * 1000;
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  if (t instanceof Date) return t.getTime();
  return 0;
};
export const storyMs = msOf;

/** Still up? */
export function isLive(story, nowMs) {
  const exp = msOf(story && story.expiresAt);
  return exp > nowMs;
}

/** May this viewer see this story? The owner; anyone for 'everyone'; friends otherwise. Never across a block. */
export function canSeeStory(story, viewer, isFriend, blocked = false) {
  if (!story || !viewer) return false;
  if (String(story.userId) === String(viewer)) return true;
  if (blocked) return false;
  if (story.audience === 'everyone') return true;
  return isFriend(String(story.userId));
}

/** How long a story stays on screen. */
export function storyDuration(story) {
  if (story && story.type === 'video') {
    const d = Number(story.durationMs) || 0;
    return d > 0 ? Math.min(VIDEO_MAX_MS, d) : 15000;
  }
  return IMAGE_MS;
}
/**
 * Stories → one ring per person: mine first, then people with something new
 * (newest first), then people I have seen everything of. Inside a ring, oldest
 * first — a story is told in order.
 */
export function groupStories(stories, me, seen = {}, nowMs = Date.now()) {
  const by = new Map();
  for (const s of stories || []) {
    if (!s || !s.id || !s.userId || !isLive(s, nowMs)) continue;
    const uid = String(s.userId);
    let g = by.get(uid);
    if (!g) {
      g = {
        uid,
        name: String(s.userName || ''),
        image: String(s.userImage || ''),
        stories: [],
        latestAt: 0,
        allSeen: true,
        mine: uid === String(me || '')
      };
      by.set(uid, g);
    }
    g.stories.push(s);
    const t = msOf(s.createdAt);
    if (t > g.latestAt) {
      g.latestAt = t;
      g.name = String(s.userName || g.name);
      g.image = String(s.userImage || g.image);
    }
    if (!seen[String(s.id)]) g.allSeen = false;
  }
  const groups = Array.from(by.values());
  for (const g of groups) g.stories.sort((a, b) => msOf(a.createdAt) - msOf(b.createdAt));
  groups.sort((a, b) => {
    if (a.mine !== b.mine) return a.mine ? -1 : 1;
    if (a.allSeen !== b.allSeen) return a.allSeen ? 1 : -1;
    return b.latestAt - a.latestAt;
  });
  return groups;
}

/** Where to start in a ring: the first story not yet seen (or the first, when all are). */
export function firstUnseen(group, seen = {}) {
  if (!group || !group.stories.length) return 0;
  const i = group.stories.findIndex(s => !seen[String(s.id)]);
  return i < 0 ? 0 : i;
}

/** A reply is words, trimmed, and not too many of them. */
export function cleanReply(text) {
  return String(text || '').replace(/\s+/g, ' ').trim().slice(0, STORY_TEXT_MAX);
}

/** "2h", "35m", "now" — how old a story is. */
export function storyAge(createdAt, nowMs) {
  const d = Math.max(0, nowMs - msOf(createdAt));
  const m = Math.floor(d / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h`;
}
